import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../salesforce/logo.svg";

export const agentforce = defineConnector({
  identifier: "agentforce",
  name: "Agentforce",
  tagLine: "Salesforce’s AI agent platform",
  logo,
  protocol: "http",
  tags: ["ai"],
  defaultUrlPattern: () => "https://.*\\.salesforce\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create an external client app",
      items: [
        {
          kind: "markdown",
          content: outdent`
            The [Agent API](https://developer.salesforce.com/docs/ai/agentforce/guide/agent-api-get-started.html) requires an external client app. In Salesforce Setup, under **External Client Apps Manager**, create one with OAuth settings enabled and:

            - The scopes **chatbot_api**, **sfap_api**, **api**, and **refresh_token, offline_access**
            - **Enable Client Credentials Flow** checked
            - **Issue JSON Web Token (JWT)-based access tokens for named users** checked

            Salesforce requires a callback URL even though the client credentials flow never redirects to it. Use this one:
          `,
        },
        {
          kind: "copyable",
          label: "Callback URL",
          value: "{API_URL}/connected-apps/oauth/agentforce/callback",
        },
        {
          kind: "markdown",
          content:
            "Then, on the app’s **Policy** tab, check **Enable Client Credentials Flow** and set **Run As** to a user with API access.",
        },
      ],
    },
    {
      title: "Enter your My Domain URL",
      items: [
        {
          kind: "hostname",
          label: "My Domain URL",
          placeholder: "mycompany.my.salesforce.com",
          required: true,
          helperText:
            "The Current My Domain URL value in Salesforce Setup under My Domain.",
          target: { kind: "configuration", field: "domain" },
        },
      ],
    },
    {
      title: "Enter your credentials",
      items: [
        {
          kind: "markdown",
          content:
            "Copy the consumer key and consumer secret from your external client app’s **OAuth settings**.",
        },
        {
          kind: "input",
          label: "Consumer key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Consumer secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ domain }) => `https://${domain}/services/oauth2/token`,
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
  },
  connectionTest: {
    protocol: "http",
    request: ({ domain }) => `https://${domain}/services/data/v62.0/`,
  },
  publicEnvVars: ({ domain }) => ({
    AGENTFORCE_API_URL: "https://api.salesforce.com/einstein/ai-agent/v1",
    SALESFORCE_MY_DOMAIN_URL: domain ? `https://${domain}` : "",
  }),
  usageHint: () =>
    "Use process.env.AGENTFORCE_API_URL as the Agent API base URL. Start a session with POST /agents/<agentId>/sessions, sending an externalSessionKey UUID and instanceConfig.endpoint set to process.env.SALESFORCE_MY_DOMAIN_URL; then POST /sessions/<sessionId>/messages, and DELETE /sessions/<sessionId> to end. The proxy injects the OAuth access token.",
});
